import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import Toggle from 'react-toggle';
import cn from 'classnames';

// Redux
import { useGetFoodCategoriesQuery } from '../../../redux/slices/foodCategorySlice';

// Components
import ReactSelect from '../../shared/ReactSelect';
import DateFilter from '../../form/DateFilter';

// Styles
import dateFilterSearchTheme from '../../form/DateFilter-search.module.postcss';
import selectSearchTheme from '../../shared/ReactSelect-search.module.postcss';
import '../../../../stylesheets/shared/components/ReactToggle.postcss';
import styles from './SearchFilters.module.postcss';

function SearchFilters({ filters, setFilters, mapOpen, setMapOpen, noMapToggle = false }) {
  const [tomorrow, setTomorrow] = useState(false);
  const [openNow, setOpenNow] = useState(false);
  const [selectedDay, setSelectedDay] = useState(null);
  const [selectedCuisine, setSelectedCuisine] = useState(null);

  const { data = {} } = useGetFoodCategoriesQuery();
  const foodCategories = data.food_categories || [];

  const onChangeCuisine = (values) => {
    const newCuisines = values.map((cuisine) => {
      return cuisine.id;
    });
    setFilters({ food_categories: newCuisines, page: null });
  };

  // Update selectedCuisine
  useEffect(() => {
    const categories = data.food_categories || [];
    let newCuisine = null;
    if (filters.food_categories) {
      const ids = Array.isArray(filters.food_categories)
        ? filters.food_categories.map(String)
        : filters.food_categories.split(',').map(s => s.trim());
      newCuisine = ids.map(id => categories.find(c => String(c.id) === id)).filter(Boolean);
      if (newCuisine.length === 0) newCuisine = null;
    }

    if (JSON.stringify(newCuisine) !== JSON.stringify(selectedCuisine)) {
      setSelectedCuisine(newCuisine);
    }
  }, [filters.food_categories, data.food_categories, selectedCuisine]);

  // Update selectedDay
  useEffect(() => {
    const newSelectedDay = filters.day ? dayjs(filters.day).toDate() : null;

    if (newSelectedDay?.toDateString() !== selectedDay?.toDateString()) {
      setSelectedDay(newSelectedDay);
    }
  }, [filters.day, selectedDay]);

  const onChangeDate = (date) => {
    if (date) {
      setFilters({
        day: dayjs(date).format('YYYY-MM-DD'),
        open_now: null,
        tomorrow: null,
        page: null,
      });
    } else {
      setFilters({ day: null, page: null });
    }
  };

  const onOpenNow = () => {
    if (filters.open_now) {
      setFilters({ open_now: null, page: null });
    } else {
      setFilters({
        open_now: true,
        tomorrow: null,
        day: null,
        page: null,
      });
    }
  };

  const onOpenTomorrow = () => {
    if (filters.tomorrow) {
      setFilters({ tomorrow: null, page: null });
    } else {
      setFilters({
        tomorrow: true,
        open_now: null,
        day: null,
        page: null,
      });
    }
  };

  // Update openNow
  useEffect(() => {
    setOpenNow(filters.open_now);
  }, [filters.open_now]);

  // Update tomorrow
  useEffect(() => {
    setTomorrow(filters.tomorrow);
  }, [filters.tomorrow]);

  return (
    <div className={styles.SearchFilters_Container + ' Section'}>
      <div className={styles.SearchFilters}>
        <ReactSelect
          theme={selectSearchTheme}
          placeholder='🍲 Cuisine'
          aria-label='Filter by cuisine'
          options={foodCategories}
          getOptionLabel={(option) => option.name}
          getOptionValue={(option) => option.id}
          isMulti
          isSearchable
          value={selectedCuisine}
          onChange={onChangeCuisine}
          menuPosition='fixed'
        />

        <button
          type='button'
          className={cn('Button', {
            [styles.SearchFilters_Button__active]: openNow,
          })}
          onClick={onOpenNow}
        >
          <span aria-hidden='true'>📢 </span>Open Now
        </button>

        <button
          type='button'
          className={cn('Button', {
            [styles.SearchFilters_Button__active]: tomorrow,
          })}
          onClick={onOpenTomorrow}
        >
          <span aria-hidden='true'>🔜 </span>Tomorrow
        </button>

        <DateFilter
          theme={dateFilterSearchTheme}
          onChange={onChangeDate}
          selectedDay={selectedDay}
          placeholder='🗓️ Choose Date'
          ariaLabel='Filter by date'
        />
      </div>

      { !noMapToggle &&
        <div className={styles.MapToggle}>
          <label htmlFor='mapToggle'>Show Map</label>
          <Toggle
            id='mapToggle'
            checked={mapOpen}
            icons={false}
            onChange={() => setMapOpen(!mapOpen)}
          />
        </div>
      }
    </div>
  );
}

SearchFilters.propTypes = {
  filters: PropTypes.object.isRequired,
  setFilters: PropTypes.func.isRequired,
};

export default SearchFilters;
